import React from 'react';
import { getBlockStyle, getTextStyle, generateTextStyleClasses, generateTextStyleInlineStyles } from 'utils/blockStyleUtils';

/**
 * TeacherNameBlock - Shared component for displaying teacher name
 *
 * Used in both edit mode (preview with placeholder) and public mode (display name).
 * Applies customizable text styles from page design.
 *
 * @param {Object} props
 * @param {string} props.displayName - Teacher's display name
 * @param {Object} props.pageDesign - Page design configuration
 * @param {Object} props.translations - I18n translations object
 * @param {string} props.mode - Display mode: 'edit' (with placeholder) or 'public' (no placeholder)
 * @param {boolean} props.showPlaceholder - Show placeholder when no name (edit mode)
 */
export default function TeacherNameBlock({
  displayName,
  pageDesign,
  translations,
  mode = 'public',
  showPlaceholder = false
}) {
  // Get block style and text style (teacher name uses title style since it's a heading)
  const blockStyle = getBlockStyle(pageDesign, 'teacher_name');
  const textStyle = getTextStyle(blockStyle, 'title');

  // Don't render if no name and not showing placeholder
  if (!displayName && !showPlaceholder) return null;

  return (
    <div className="teacher-name-block text-center">
      {displayName ? (
        <h1
          className={`teacher-name-text ${generateTextStyleClasses(textStyle, pageDesign)}`}
          style={generateTextStyleInlineStyles(textStyle)}
        >
          {displayName}
        </h1>
      ) : (
        <p className="placeholder text-gray-400 text-sm italic">
          {translations?.block_previews?.teacher_name_placeholder || 'Click to edit and add teacher name'}
        </p>
      )}

      <style jsx>{`
        .teacher-name-block {
          /* Padding removed - EditableBlock handles unified p-8 padding */
        }

        .teacher-name-text {
          margin: 0;
        }

        .placeholder {
          margin: 0;
        }
      `}</style>
    </div>
  );
}
